/*
 * supertime-date-gate.js
 *
 * The Supertime ("Powered by Roundtrip") app requires a delivery date before
 * checkout, but out of the box it:
 *   1. renders its "You must select a delivery date" error in red on page load,
 *      before the shopper has done anything, and
 *   2. blocks the checkout submit without any feedback at the button, leaving
 *      the shopper at the top of the page with no idea why nothing happened.
 *
 * This progressively enhances that behaviour. It does not replace Supertime's
 * own validation - it runs ahead of it so the shopper gets feedback where they
 * clicked. If the app changes its markup, the guards below simply no-op and the
 * app's native behaviour is unaffected.
 */
(function () {
  if (window.__supertimeDateGateInit) return;
  window.__supertimeDateGateInit = true;

  var BLOCK = '.supertime';
  var DATE_INPUT = 'input[name="supertime-date-picker"], #supertime-date-picker';
  var REQUIRED_FLAG = '.supertime-date-required';
  var ERROR_TEXT = '.supertime-required-text';
  var IDLE_CLASS = 's-date-gate--idle';
  var INVALID_CLASS = 's-date-gate--invalid';

  /* The cart page and the cart drawer each render their own Supertime block,
     and both use the same element ids. Resolve strictly by context: anything
     inside <cart-drawer> belongs to the drawer's block, anything else belongs
     to the cart page's block.

     Never fall back to a plain document.querySelector here - that returns the
     drawer's block (it comes first in the DOM), which would validate the cart
     page's checkout against an empty field the shopper cannot even see. */
  /* Returns EVERY block belonging to the element's context, not just the first.

     The app is observed to render more than one block into a single context: the drawer
     footer's block is destroyed on every Section Rendering API re-render and re-injected,
     and a stale copy can survive that. Returning only the first match meant a filled block
     could satisfy the gate while an empty required one sat directly above the checkout
     button, and because both carry the same `name` the empty one can be what serializes.
     That is the date-less order this gate exists to prevent. */
  function blocksForElement(el) {
    if (!el) return [];

    var blocks = Array.prototype.slice.call(document.querySelectorAll(BLOCK));
    if (!blocks.length) return [];

    var wantDrawer = !!(el.closest && el.closest('cart-drawer'));

    return blocks.filter(function (block) {
      return !!block.closest('cart-drawer') === wantDrawer;
    });
  }


  function isRequired(block) {
    return !!(block && block.querySelector(REQUIRED_FLAG));
  }

  function dateInput(block) {
    return block ? block.querySelector(DATE_INPUT) : null;
  }

  function hasDate(block) {
    var input = dateInput(block);
    return !!(input && input.value && input.value.trim());
  }

  /* Keep the error suppressed until the shopper has actually tried to check
     out, or has touched the date field and left it empty. */
  function markIdle(block) {
    if (!block || !block.querySelector(ERROR_TEXT)) return;
    block.classList.add(IDLE_CLASS);
    block.classList.remove(INVALID_CLASS);
  }

  function markInvalid(block) {
    if (!block) return;
    block.classList.remove(IDLE_CLASS);
    block.classList.add(INVALID_CLASS);
  }

  function clearInvalid(block) {
    if (!block) return;
    block.classList.remove(INVALID_CLASS);
    block.classList.add(IDLE_CLASS);
  }

  function focusDateField(block) {
    var input = dateInput(block);
    if (!input) return;

    var reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    input.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'center',
    });

    /* Give the smooth scroll a beat before focusing, otherwise the browser
       jumps and cancels the animation. */
    setTimeout(function () {
      try {
        input.focus({ preventScroll: true });
      } catch (e) {
        input.focus();
      }
    }, reduceMotion ? 0 : 320);
  }

  function initBlocks() {
    var blocks = document.querySelectorAll(BLOCK);
    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i];
      if (block.dataset.dateGateReady) continue;
      block.dataset.dateGateReady = 'true';

      if (isRequired(block) && !hasDate(block)) {
        markIdle(block);
      } else {
        clearInvalid(block);
      }
    }
  }

  /* Intercept ahead of the app so we can report the problem at the button.
     The click handler is the only gate: it covers the real interaction, and
     Supertime still runs its own validation on submit. Adding a submit-phase
     gate as well is what previously blocked valid checkouts, because a form
     does not necessarily contain its own Supertime block. */
  function onCheckoutAttempt(event) {
    var button = event.target.closest
      ? event.target.closest('[name="checkout"]')
      : null;
    if (!button) return;

    var blocks = blocksForElement(button);
    if (!blocks.length) return;

    /* Every required block in this context has to carry a date, not just the first one. */
    var unsatisfied = blocks.filter(function (block) {
      return isRequired(block) && !hasDate(block);
    });

    if (!unsatisfied.length) {
      blocks.forEach(clearInvalid);
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }

    unsatisfied.forEach(markInvalid);
    focusDateField(unsatisfied[0]);
  }

  document.addEventListener('click', onCheckoutAttempt, true);

  /* Clear the error as soon as a date lands in the field. Supertime writes the
     value programmatically and renders its calendar outside the block, so poll
     every block briefly after any interaction rather than relying on a change
     event or on the click target sitting inside the block. */
  function sweepResolved() {
    var blocks = document.querySelectorAll(BLOCK);
    for (var i = 0; i < blocks.length; i++) {
      if (hasDate(blocks[i])) clearInvalid(blocks[i]);
    }
  }

  function sweepAfterInteraction() {
    var checks = 0;
    var timer = setInterval(function () {
      checks++;
      sweepResolved();
      if (checks > 10) clearInterval(timer);
    }, 150);
  }

  /* Capture phase: Supertime's calendar stops propagation on day clicks, so a
     bubble-phase listener never fires and the invalid styling would stick
     after a valid date was chosen. */
  document.addEventListener('change', sweepResolved, true);
  document.addEventListener('input', sweepResolved, true);
  document.addEventListener('click', sweepAfterInteraction, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBlocks);
  } else {
    initBlocks();
  }

  /* The drawer and cart footer re-render via the Section Rendering API. */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      setTimeout(initBlocks, 0);
    });
  }

  new MutationObserver(function () {
    initBlocks();
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
